import type { PoLine, ScanLine } from '../api/types';

/**
 * Barcode matching, mirrored from the server's domain/scan-match.ts.
 *
 * It runs client-side so a scan resolves instantly without a round trip —
 * a warehouse operator scans faster than the network answers. The server
 * re-validates everything on submit, so this copy is purely for responsiveness
 * and is never the authority.
 */

export interface Candidate {
  line: PoLine;
  qtyAlreadyEntered: number;
  qtyRemaining: number;
}

export type ScanResult =
  | { kind: 'not-ordered'; barcode: string }
  | { kind: 'already-complete'; barcode: string }
  | { kind: 'single'; barcode: string; candidate: Candidate }
  | { kind: 'choose'; barcode: string; candidates: Candidate[] };

export function matchBarcode(
  lines: readonly PoLine[],
  rawBarcode: string,
  entered: ReadonlyMap<string, number>,
  receivedElsewhere: Readonly<Record<string, number>>,
): ScanResult {
  const barcode = rawBarcode.trim();
  if (!barcode) return { kind: 'not-ordered', barcode };

  // Stage 1: exact UPC or SKU.
  let matched = lines.filter((l) => l.barcode === barcode || l.sku === barcode);

  // Stage 2: variant interception — the carton shows the base code while
  // Accurate holds '<base>-RED', '<base>-XL' and friends.
  if (matched.length === 0) {
    matched = lines.filter((l) => l.barcode.startsWith(`${barcode}-`));
  }
  if (matched.length === 0) return { kind: 'not-ordered', barcode };

  const candidates: Candidate[] = matched.map((line) => {
    const qtyAlreadyEntered = entered.get(line.poDetailId) ?? 0;
    const elsewhere = alreadyReceived(line, receivedElsewhere);
    const remaining = line.qtyOrdered - qtyAlreadyEntered - elsewhere;
    return { line, qtyAlreadyEntered, qtyRemaining: remaining > 0 ? remaining : 0 };
  });

  // Stage 3: drop lines already satisfied.
  const open = candidates.filter((c) => c.qtyRemaining > 0);
  if (open.length === 0) return { kind: 'already-complete', barcode };
  if (open.length === 1) return { kind: 'single', barcode, candidate: open[0]! };
  return { kind: 'choose', barcode, candidates: open };
}

/**
 * Mirror of alreadyReceived() in server/src/domain/po.ts -- see that copy for
 * why this is max() and not `??` or `+`.
 *
 * Duplicated because the scan matcher is deliberately duplicated: the browser
 * must decide what a scan means without a round trip, and the server must not
 * trust it. The server's answer is the one that governs; this one has to agree
 * or the operator is shown a ceiling the submit will then reject.
 */
export function alreadyReceived(
  line: Pick<PoLine, 'poDetailId' | 'qtyReceivedAol'>,
  receivedElsewhere: Record<string, number>,
): number {
  return Math.max(receivedElsewhere[line.poDetailId] ?? 0, line.qtyReceivedAol ?? 0);
}

export function isBonusLine(line: PoLine): boolean {
  return line.itemName.toLowerCase().includes('bonus');
}

export function totalQty(lines: readonly ScanLine[]): number {
  return lines.reduce((sum, l) => sum + l.qtyReceived, 0);
}

/**
 * A short square-wave beep on a rejected scan.
 *
 * Carried over deliberately: in a noisy warehouse the operator is looking at
 * the carton, not the screen, and the sound is the only feedback they get.
 */
export function playErrorBeep(): void {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
    // Release the hardware audio context once the tone has finished.
    osc.onended = () => void ctx.close().catch(() => undefined);
  } catch {
    // Audio is a nicety; never let it break a scan.
  }
}
